import type { LucideIcon } from "lucide-react";
import { classNames } from "@/lib/format";

export interface StammdatenTabDef<TId extends string = string> {
  id: TId;
  label: string;
  /** Kürzere Variante für schmale Bildschirme */
  shortLabel?: string;
  /** Ein Satz, was in diesem Tab eingestellt wird */
  description?: string;
  icon?: LucideIcon;
}

interface StammdatenTabNavProps<TId extends string> {
  tabs: StammdatenTabDef<TId>[];
  active: TId;
  onChange: (id: TId) => void;
  ariaLabel: string;
}

/** Einheitliche Tab-Leiste für Stammdaten-Seiten mit mehreren Abschnitten */
export function StammdatenTabNav<TId extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
}: StammdatenTabNavProps<TId>) {
  const activeTab = tabs.find((tab) => tab.id === active);

  return (
    <nav aria-label={ariaLabel} className="space-y-2">
      <div
        role="tablist"
        className="flex flex-wrap gap-1 rounded-xl border border-separator bg-grouped p-1"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === active;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              title={tab.description}
              className={classNames(
                "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-accent text-label-on-accent shadow-sm"
                  : "text-label-secondary hover:bg-fill hover:text-label",
              )}
              onClick={() => onChange(tab.id)}
            >
              {Icon ? <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0" /> : null}
              <span className={tab.shortLabel ? "hidden sm:inline" : undefined}>{tab.label}</span>
              {tab.shortLabel ? <span className="sm:hidden">{tab.shortLabel}</span> : null}
            </button>
          );
        })}
      </div>
      {activeTab?.description ? (
        <p className="text-sm text-label-secondary">{activeTab.description}</p>
      ) : null}
    </nav>
  );
}
